/**
 * Relevance filter — ARCHITECTURE.md section 6.3.
 *
 * First gate in the extraction pipeline. Uses Haiku (fast, cheap)
 * to score raw text against desk priority indicators.
 * Text scoring below the threshold is skipped, preventing
 * waste of Sonnet tokens on irrelevant content.
 */

import type { PriorityIndicator } from '@/config/desk-config';
import type {
  LLMAdapter,
  RelevanceResult,
  LLMCallResult,
} from './llm-adapter';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

const DEFAULT_THRESHOLD = 0.3;

export interface RelevanceFilterConfig {
  threshold?: number;
}

// ---------------------------------------------------------------------------
// Filter result
// ---------------------------------------------------------------------------

export interface FilteredText {
  text: string;
  relevance: RelevanceResult;
  passed: boolean;
}

// ---------------------------------------------------------------------------
// Relevance filter
// ---------------------------------------------------------------------------

export interface RelevanceFilter {
  /**
   * Score a single text against desk priority indicators.
   * Returns the relevance result regardless of threshold.
   */
  score(text: string): Promise<LLMCallResult<RelevanceResult>>;

  /**
   * Filter a text — returns whether it passed the threshold
   * along with the full relevance result.
   */
  filter(text: string): Promise<FilteredText>;

  /**
   * Filter a batch of texts. Returns only those that pass the threshold.
   * Processes sequentially to respect rate limits.
   */
  filterBatch(texts: string[]): Promise<FilteredText[]>;

  /** Get the current threshold. */
  readonly threshold: number;
}

/**
 * Create a relevance filter for an investigation.
 *
 * @param adapter - LLM adapter instance (with its own token tracker)
 * @param indicators - Priority indicators from the active desk config
 * @param config - Optional configuration (threshold)
 */
export function createRelevanceFilter(
  adapter: LLMAdapter,
  indicators: PriorityIndicator[],
  config?: RelevanceFilterConfig,
): RelevanceFilter {
  const threshold = config?.threshold ?? DEFAULT_THRESHOLD;

  return {
    get threshold() {
      return threshold;
    },

    async score(text) {
      return adapter.classifyRelevance(text, indicators);
    },

    async filter(text) {
      const result = await adapter.classifyRelevance(text, indicators);
      return {
        text,
        relevance: result.data,
        passed: result.data.score >= threshold,
      };
    },

    async filterBatch(texts) {
      const results: FilteredText[] = [];

      for (const text of texts) {
        try {
          const filtered = await this.filter(text);
          results.push(filtered);
        } catch (err) {
          // On LLM failure, include the text with a zero score
          // so it can be retried or manually reviewed
          console.warn(
            '[Sentinel] Relevance filter failed for text:',
            err instanceof Error ? err.message : String(err),
          );
          results.push({
            text,
            relevance: { score: 0, matchedIndicatorIds: [] },
            passed: false,
          });
        }
      }

      return results;
    },
  };
}
